import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { truncatePreservingNewlines } from '../utils/textHelpers';
import Icon from './Icon';

/* ───────────── deterministic pseudo-random helpers ─────────────
   Same note id always yields the same tilt, so nothing jitters
   on re-render / re-sort. djb2-style hash. */
const hash = (str = '') => {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) + str.charCodeAt(i);
  return Math.abs(h);
};

/* Rotation range: ±2.2° — a touch less than photos (±3.2°). Paper
   stacks on a surface flatter than polaroids shuffled in a drawer,
   and lower-weight text feels uncomfortable at large tilt values. */
const seededRotation = (id) => {
  const h = hash(id + 'note-rot');
  return ((h % 45) / 10) - 2.2;
};

/**
 * A tactile, paper-style memory-note card.
 *
 * Visual twin of `PhotoCard` for the notes gallery:
 *   • Same deterministic tilt seeded from the note id
 *   • Same three-layer warm shadow stack
 *   • Same stagger/entrance animation and tap-flatten
 *
 * Internals differ: a note is a *single* white paper surface (no
 * inner image well), with date + body + author laid out vertically.
 * The whole card is a button.
 *
 * Props:
 *   note        { id, text, authorName, createdAt } — required
 *   date        string — preformatted date for the header pill
 *   onClick     () => void
 *   isSelected  boolean — admin-mode selection
 *   index       number  — fallback seed + stagger slot
 *   plain       boolean = false — disable rotation (list view)
 *   maxWords    number  — truncate body past N words
 */
export default function NoteCard({
  note,
  date,
  onClick,
  isSelected = false,
  index = 0,
  plain = false,
  maxWords,
}) {
  const seed = note?.id || String(index);
  const rotation = useMemo(() => (plain ? 0 : seededRotation(seed)), [seed, plain]);

  // Stagger the first ~10 cards, then snap the rest in instantly.
  // Matches PhotoCard so the two gallery pages read as one system.
  const delay = Math.min(index * 0.025, 0.25);

  const authorRaw = note?.authorName || note?.author || 'Anonymous';
  const author = authorRaw === 'Anonym' ? 'Anonymous' : authorRaw;

  const bodyRaw = note?.text || '';
  const bodyTrimmed = truncatePreservingNewlines(bodyRaw, maxWords);
  const paragraphs = String(bodyTrimmed || '').split(/\n+/).filter(Boolean);
  const isGallery = !plain;

  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, y: 20, scale: 0.94, rotate: 0 }}
      animate={{ opacity: 1, y: 0, scale: 1, rotate: rotation }}
      transition={{ type: 'spring', stiffness: 280, damping: 24, mass: 0.7, delay }}
      whileTap={{
        rotate: 0,
        scale: 0.98,
        transition: { type: 'spring', stiffness: 400, damping: 20 },
      }}
      className="relative block w-full text-left focus:outline-none"
      style={{
        transformOrigin: 'center center',
        // Matches PhotoCard's outer padding so a notes grid and a
        // photos grid have the same gutters and bleed.
        padding: 9,
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
      }}
    >
      <div
        className="relative min-w-0 bg-white"
        style={{
          padding: 20,
          borderRadius: 22,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          // Identical shadow stack to PhotoCard: a tight contact
          // shadow, a mid-range ambient, and a long soft tail —
          // so the card grounds like paper on a warm surface.
          boxShadow:
            '0 1px 2px rgba(64, 42, 20, 0.08), ' +
            '0 8px 18px rgba(64, 42, 20, 0.16), ' +
            '0 2px 4px rgba(64, 42, 20, 0.08)',
        }}
      >
        {/* Date row ────────────────────────────────────────────── */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            borderRadius: 100,
          }}
        >
          <span
            aria-hidden
            style={{
              width: 4,
              height: 4,
              borderRadius: '50%',
              backgroundColor: '#E09053',
              flexShrink: 0,
            }}
          />
          <span
            style={{
              color: '#B9A68E',
              fontSize: 12,
              fontFamily: 'Poppins',
              fontWeight: 500,
              letterSpacing: '0.01em',
            }}
          >
            {date}
          </span>
        </div>

        {/* Body — tighter preview in gallery mode to keep cards balanced. */}
        <div
          className="min-w-0 overflow-hidden"
          style={{
            alignSelf: 'stretch',
            maxHeight: plain ? '14rem' : '9rem',
          }}
        >
          {paragraphs.length > 0 ? (
            isGallery ? (
              <p
                style={{
                  color: '#2A1B16',
                  fontSize: 15,
                  fontFamily: 'Poppins',
                  fontWeight: 400,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-line',
                }}
              >
                {bodyTrimmed}
              </p>
            ) : (
              paragraphs.map((p, i) => (
                <p
                  key={i}
                  style={{
                    color: '#2A1B16',
                    fontSize: 15,
                    fontFamily: 'Poppins',
                    fontWeight: 400,
                    lineHeight: 1.6,
                    marginBottom: i === paragraphs.length - 1 ? 0 : 12,
                  }}
                >
                  {p}
                </p>
              ))
            )
          ) : (
            <p
              style={{
                color: '#8E7C69',
                fontSize: 15,
                fontFamily: 'Poppins',
                fontStyle: 'italic',
                lineHeight: 1.6,
              }}
            >
              (empty note)
            </p>
          )}
        </div>

        {/* Author pill — max-width + min-w-0 so ellipsis works inside flex */}
        <div
          className="inline-flex min-w-0 max-w-full items-center gap-2 self-start"
          style={{
            padding: '6px 12px',
            backgroundColor: 'rgba(68, 35, 30, 0.05)',
            borderRadius: 100,
          }}
        >
          <Icon name="author" size={12} color="#8E7C69" className="shrink-0" />
          <span
            className="min-w-0 flex-1 truncate text-left"
            title={author}
            style={{
              color: '#8E7C69',
              fontSize: 12,
              fontFamily: 'Poppins',
              fontWeight: 500,
              letterSpacing: '0.01em',
            }}
          >
            {author}
          </span>
        </div>

        {/* Admin-mode selection indicator ────────────────────── */}
        {isSelected && (
          <div
            className="absolute flex items-center justify-center"
            style={{
              top: 12,
              right: 12,
              width: 24,
              height: 24,
              borderRadius: 999,
              background: '#44231E',
              boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            }}
          >
            <Check size={13} color="#FFFFFF" strokeWidth={2.5} />
          </div>
        )}
      </div>
    </motion.button>
  );
}
